import apiClient from './api'

export const teamService = {

  async getTeams(page = 1, limit = 9, search = '') {
    const response = await apiClient.get('/teams', {
      params: {
        page,
        limit,
        search
      }
    })
    return response.data
  },

  async getTeam(teamId) {
    const response = await apiClient.get(`/teams/${teamId}`)
    return response.data
  },

  async getEligibleTeamsForUpgrade() {
    const response = await apiClient.get('/teams/eligible-upgrades')
    return response.data
  },

  async createTeam(teamData) {
    const response = await apiClient.post('/teams', teamData)
    if(Array.isArray(response.data)) {
      return response.data[0]
    }
    return response.data

  },

  async updateTeam(teamId, teamData) {
    const response = await apiClient.put(`/teams/${teamId}`, teamData)
    return response.data
  },


  async deleteTeam(teamId) {
    const response = await apiClient.delete(`/teams/${teamId}`)
    return response.data
  },

  async createInvitationCode(teamId, options = {}) {
    const response = await apiClient.post(`/invitations/codes/${teamId}`, options)
    return response.data
  },

  async getTeamInvitationCodes(teamId, page = 1, limit = 10, search = '', status = 'active') {
    const params = { page, limit, status }
    if (search) params.search = search
    
    const response = await apiClient.get(`/invitations/codes/${teamId}`, { params })
    return response.data
  },

  async validateInvitationCode(code) {
    const response = await apiClient.get(`/invitations/validate/${code}`)
    return response.data
  },

  async createJoinRequest(code, userFieldResponses = {}) {
    const response = await apiClient.post(`/invitations/join/${code}`, { userFieldResponses })
    return response.data
  },

  async deactivateInvitationCode(invitationId) {
    const response = await apiClient.post(`/invitations/${invitationId}/deactivate`)
    return response.data
  },


  async getTeamJoinRequests(teamId, status = null, page = 1, limit = 10, search = '') {
    const params = { page, limit }
    if (status) params.status = status
    if (search) params.search = search
    
    const response = await apiClient.get(`/invitations/requests/${teamId}`, { params })
    return response.data
  },

  async approveJoinRequest(requestId) {
    const response = await apiClient.post(`/invitations/requests/${requestId}/approve`)
    return response.data
  },

  async rejectJoinRequest(requestId, reason = null) {
    const response = await apiClient.post(`/invitations/requests/${requestId}/reject`, { reason })
    return response.data
  },


  async getUserJoinRequests(status = null, page = 1, limit = 10, search = '') {
    const params = { page, limit }
    if (status) params.status = status
    if (search) params.search = search
    
    const response = await apiClient.get('/invitations/my-requests', { params })
    return response.data
  },

  async cancelJoinRequest(requestId) {
    const response = await apiClient.delete(`/invitations/request/${requestId}`)
    return response.data
  },


  async getTeamReceipts(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/receipts`)
    return response.data
  },


  async getTeamSchema(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/schema`)
    return response.data
  },


  async updateTeamSchema(teamId, schemaData) {
    const response = await apiClient.put(`/teams/${teamId}/schema`, schemaData)
    return response.data
  },


  async updateTeamFieldResponse(fieldId, value) {
    const response = await apiClient.put(`/teams/responses/${fieldId}`, { value })
    return response.data
  },

  async deleteTeamFieldResponse(fieldId) {
    const response = await apiClient.delete(`/teams/responses/${fieldId}`)
    return response.data
  },

  async getTeamSepaSettings(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/sepa-settings`)
    return response.data
  },

  async saveTeamSepaSettings(teamId, settings) {
    const response = await apiClient.put(`/teams/${teamId}/sepa-settings`, settings)
    return response.data
  },

  async uploadTeamLogo(teamId, imageFile) {
    const formData = new FormData()
    formData.append('image', imageFile)
    
    const response = await apiClient.post(`/teams/${teamId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  async removeTeamLogo(teamId) {
    const response = await apiClient.delete(`/teams/${teamId}/logo`)
    return response.data
  },

  async getTeamLogo(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/logo`, {
      responseType: 'blob'
    })
    return response.data
  },


  async getTeamRefundSettings(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/refund-settings`)
    return response.data
  },

  async updateTeamRefundSettings(teamId, settings) {
    const response = await apiClient.put(`/teams/${teamId}/refund-settings`, settings)
    return response.data
  },

  async getUserRefundData(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/refund-data`)
    return response.data
  },

  async saveUserRefundData(teamId, refundData) {
    const response = await apiClient.post(`/teams/${teamId}/refund-data`, refundData)
    return response.data
  },

  async getTeamRefundData(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/refund-data/all`)
    return response.data
  },

  async getLastTeamBillingData(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/billing-data`)
    return response.data
  },

  async upgradeTeam(teamId, plan, billingData, couponCode = null, paymentMethod = 'paypal') {
    const response = await apiClient.post(`/teams/${teamId}/upgrade`, {
      plan,
      billingData,
      couponCode,
      paymentMethod
    })
    return response.data
  },

  async getTeamBillings(teamId, page = 1, limit = 20, filters = {}) {
    const params = { page, limit }
    if (filters.status) params.status = filters.status
    if (filters.plan) params.plan = filters.plan
    
    const response = await apiClient.get(`/teams/${teamId}/billings`, { params })
    return response.data
  },

  async downloadTeamBillingPDF(billingId) {
    const response = await apiClient.get(`/teams/billings/${billingId}/download`, {
      responseType: 'blob'
    })
    return response
  },

  async removeMember(teamId, userId) {
    const response = await apiClient.delete(`/teams/${teamId}/members/${userId}`)
    return response.data
  },

  async changeMemberRole(teamId, userId, role) {
    const response = await apiClient.put(`/teams/${teamId}/members/${userId}/role`, { role })
    return response.data
  },

  /**
   * Excel-Export für Team-Mitglieder
   */
  async exportTeamMembersExcel(teamId, selectedColumns = [], selectedMemberIds = []) {
    const response = await apiClient.post(`/teams/${teamId}/export/members/excel`, {
      selectedColumns,
      selectedMemberIds
    }, {
      responseType: 'blob'
    })
    return response
  },

  /**
   * ZIP-Export für Quittungen eines Mitglieds
   */
  async exportMemberReceiptsZip(teamId, userId) {
    const response = await apiClient.get(`/teams/${teamId}/export/members/${userId}/receipts/zip`, {
      responseType: 'blob'
    })
    return response
  },

  /**
   * Team-Mitglieder mit serverseitiger Filterung abrufen
   */
  async getTeamMembers(teamId, options = {}) {
    const {
      page = 1,
      limit = 25,
      search = '',
      roleFilter = '',
      sortField = 'name',
      sortDirection = 'asc'
    } = options;

    const params = {
      page,
      limit,
      search,
      roleFilter,
      sortField,
      sortDirection
    };

    
    Object.keys(params).forEach(key => {
      if (!params[key] && params[key] !== 0) {
        delete params[key];
      }
    });

    const response = await apiClient.get(`/teams/${teamId}/members`, { params });
    return response.data;
  },

  async updateTeamReceiptSettings(teamId, settings) {
    const response = await apiClient.put(`/teams/${teamId}/receipt-settings`, settings)
    return response.data
  },


} 
